/* eslint-disable multiline-ternary */
import {
  Map,
  PickupPointsList,
  PointDataConfirmation,
  Spinner
} from '@pinflag/pinflag-ui-kit'
import React, { useContext, useEffect, useState } from 'react'

import Autocomplete from '../Autocomplete'
import { DataContext } from '../../context/DataContext'
import ReturnPickupPoint from '../ReturnPickupPoint'
import { getUserLocation } from '../../helpers/initialGeolocatorHelper'
import { sortGeojson } from '../../helpers/sortGeojson'
import { scrollToTop } from '../../helpers/utils'
import { STORES_DISCOUNT_CYC, ONLY_CHECKOUT } from '../../constants/stores'
import {
  editDeliveryTimeLocalPickup
} from '../../helpers/filterPoints'
import { AddressConfirmation } from '../AddressConfirmation'
import { useApiPinflag } from '../../hook/useApiPinflag'
import { useCmsAdapter } from '../../hook/useCmsAdapter'
import { useVtex } from '../../hook/useVtex'
import { getAddressObject } from '../../helpers/addressParser'
import { gaPaymentPostMessage } from '../../helpers/gaEventsHelper'

const PickupMap = () => {
  const {
    selectedShippingMethod,
    setIsPickup,
    steps,
    setSteps,
    pointsData,
    setPointsData,
    setAddressInfo,
    setIsPaying,
    companyInfo,
    setCartData,
    cartData,
    ecommerceInfo,
    setSelectedServiceType
  } = useContext(DataContext)

  const { companyName, secondaryColor } = companyInfo

  const { serviceClickAndCollect } = useApiPinflag()

  const { postTrackingEvent } = useCmsAdapter()

  const { getPickupPoints, saveShippingInformationVtex } = useVtex()

  const {
    pinflagPoints,
    localPoints,
    localPointSelected,
    pinflagPointSelected
  } = pointsData

  const isLocalPickup = selectedShippingMethod === 'local-pickup'

  const points = isLocalPickup
    ? editDeliveryTimeLocalPickup(localPoints, companyName)
    : pinflagPoints

  const pointSelected = isLocalPickup
    ? localPointSelected
    : pinflagPointSelected

  const [markerSelectedId, setMarkerSelectedId] = useState(
    pointSelected?.properties ? pointSelected.properties.id : null
  )

  const initialPoint = [-70.610672, -33.417752]

  const [mapCenter, setMapCenter] = useState(
    pointSelected?.properties
      ? pointSelected.geometry.coordinates
      : initialPoint
  )

  const [orderedPoints, setOrderedPoints] = useState([])

  const [isSummary, setIsSummary] = useState(false)
  const [isReady, setIsReady] = useState(true)

  const [pointInfo, setPointInfo] = useState(
    pointSelected?.properties ? pointSelected.properties : null
  )

  const [pressed, setPressed] = useState(true)

  const [filteredPoints, setFilteredPoints] = useState([])

  useEffect(() => {
    setIsPickup(true)
    setOrderedPoints(sortGeojson(points, mapCenter))
    !markerSelectedId && getUserLocation(setInitialPosition)
    postTrackingEvent('impression', {
      currentCheckoutSubstep: `2 - ${selectedShippingMethod}`,
      previousCheckoutStep: '2',
      previousCheckoutSubstep: '1'
    })
  }, [])

  useEffect(() => {
    if (orderedPoints.length) {
      const newFilteredPoints = isLocalPickup ? orderedPoints : orderedPoints.slice(0, 7)
      setFilteredPoints(disablePointPricesForVitel(newFilteredPoints))
    }
  }, [orderedPoints])

  const handlePlaceSelected = async (place) => {
    setIsReady(false)
    setMapCenter(place.center)
    setMarkerSelectedId(null)
    let newPoints
    if (ONLY_CHECKOUT.includes(companyInfo.companyName)) {
      const addressObject = getAddressObject(place)
      newPoints = await getPickupPoints(addressObject)
    } else {
      newPoints = sortGeojson(orderedPoints, place.center)
    }
    setOrderedPoints(newPoints)
    setFilteredPoints(isLocalPickup ? newPoints : newPoints.slice(0, 7))
    setIsReady(true)
  }

  const handlePointSelection = (data) => {
    // returns a list of all the points. if not filtered, creates and sets on first position.
    if (data?.properties?.id) {
      orderPointsList(data.properties.id)
      setMarkerSelectedId(data.properties.id)
    }
    if (data?.properties) {
      setPointInfo(data.properties)
    }
    setMapCenter(data.geometry.coordinates)

    const updatedSteps = [...steps]
    updatedSteps[0].completed = true
    updatedSteps[1].disabled = false
    updatedSteps[1].subSteps[0].completed = true
    updatedSteps[1].subSteps[1].completed = true
    updatedSteps[1].subSteps[2].completed = true
    setSteps(updatedSteps)

    setPointsData((prevState) => {
      prevState[isLocalPickup ? 'localPointSelected' : 'pinflagPointSelected'] =
        data
      return prevState
    })
    scrollToTop()
  }

  const orderPointsList = (selectedId) => {
    const masterIndex = orderedPoints.findIndex(
      (p) => p.properties.id === selectedId
    )
    const filteredIndex = filteredPoints.findIndex(
      (p) => p.properties.id === selectedId
    )
    // This function should execute when the point is not currently on the list
    if (filteredIndex !== -1) {
      filteredPoints.unshift(filteredPoints[filteredIndex])
      filteredPoints.splice(filteredIndex + 1, 1)
      setFilteredPoints(filteredPoints)
    } else {
      const updatedList = [...filteredPoints]
      updatedList.unshift(orderedPoints[masterIndex])
      setFilteredPoints(updatedList)
    }
  }

  const disablePointPricesForVitel = (points) => {
    return points.map((point) => {
      // If company is Vitel Energia and the total weight is greater than 20, do not show the price
      point.properties.showPrice = !(companyName === 'Vitel Energia' && cartData.totalWeight > 20)
      return point
    })
  }

  const setInitialPosition = (location) => {
    const { longitude, latitude } = location.coords
    setMapCenter([longitude, latitude])
    setOrderedPoints(sortGeojson(points, [longitude, latitude]))
  }

  const handleDeploymentPickupList = (e) => {
    if (e.target) {
      setPressed(true)
    } else {
      setPressed(false)
    }
  }

  const showSummary = () => {
    if (ecommerceInfo.cms === 'woocommerce') {
      setIsSummary(true)
    } else {
      setIsPaying(true)
    }
    setIsSummary(true)
  }

  const handlePointConfirmation = (point) => {
    if (ONLY_CHECKOUT.includes(companyInfo.companyName)) {
      saveShippingInformationVtex(point)
    } else {
      serviceClickAndCollect(point).then((service) => {
        setSelectedServiceType(service)
        setAddressInfo(prevState => ({
          ...prevState,
          userAddress: point.address,
          aditionalInfo: '',
          region: point.region,
          district: point.district,
          price: point.price,
          deliveryType: isLocalPickup ? 'Retiro en Tienda' : 'Retiro en punto',
          sla: service.description,
          courier: point.company,
          mapCoords: mapCenter,
          serviceName: service.name
        }))
        gaPaymentPostMessage()
        setCartData(prevState => {
          return {
            ...prevState,
            shippingCost: point.price,
            total: (cartData.subtotal || 0) + point.price
          }
        })
        showSummary()
        postTrackingEvent('click - submit', {
          currentCheckoutSubstep: `2 - ${selectedShippingMethod}`,
          previousCheckoutStep: '2',
          previousCheckoutSubstep: '1'
        })
      })
    }
  }

  if (!ONLY_CHECKOUT.includes(companyName) && !orderedPoints.length) {
    return (
      <div className='full_width_spinner md:w-[537px]'>
        <div className='md:flex md:justify-center'>
          <Spinner width={44} />
        </div>
      </div>
    )
  }

  return (
    <>
      {isSummary ? <>
        <AddressConfirmation mapCenter={mapCenter} />
      </>
        : <>
          <div className='md:hidden'>
            <div className='w-full bg-slate-100 flex flex-col items-center py-2'>
              <div className='flex md:absolute md:left-1/2 md:-translate-x-1/2 z-10 w-11/12 md:border-2 rounded-xl text-xs bg-white'>
                <ReturnPickupPoint />
              </div>
              <Autocomplete
                onPlaceSelected={handlePlaceSelected}
                showNoResultsAction={true}
              />
            </div>
            <div className='h-full' onClick={(e) => handleDeploymentPickupList(e)}>
              <Map
                markerSource={{ features: orderedPoints }}
                markerSelectedId={markerSelectedId}
                onPointSelection={handlePointSelection}
                center={mapCenter}
                height={'17rem'}
                setIsReady={setIsReady}
                mapboxToken={companyInfo.mapboxToken}
              />
            </div>
            <div>
              {markerSelectedId || !pressed ? (
                <div className='rounded-xl bg-white bottom-0 z-10 w-full relative -mt-24 ease-linear duration-100'>
                  {companyInfo.companyName === 'Agua de Coco' && isLocalPickup && <span className='flex justify-center text-center bg-white text-sm'>Envíos a tienda MIE o VIE. <br />Avisamos telefónicamente su llegada.</span>}
                  {!isReady ? <div className='flex flex-col justify-center h-[250px]'> <Spinner /> </div>
                    : <PickupPointsList
                      points={filteredPoints}
                      onPointSelection={handlePointSelection}
                      markerSelectedId={markerSelectedId}
                      setMarkerSelectedId={setMarkerSelectedId}
                      isLocalPickup={isLocalPickup}
                      pressed={pressed}
                      setPressed={setPressed}
                      handlePointConfirmation={handlePointConfirmation}
                      secondaryColor={secondaryColor}
                      discount={STORES_DISCOUNT_CYC[companyName] || 40}
                      height={'47vh'}
                      id="map-payment-button"
                    />
                  }
                </div>
              ) : (
                <div className='rounded-xl bg-white bottom-0 z-10 w-full relative -mt-1.5 ease-linear duration-100'>
                  {!isReady ? <div className='flex flex-col justify-center h-[250px]'> <Spinner /> </div>
                    : <PickupPointsList
                      points={filteredPoints}
                      onPointSelection={handlePointSelection}
                      markerSelectedId={markerSelectedId}
                      setMarkerSelectedId={setMarkerSelectedId}
                      isLocalPickup={isLocalPickup}
                      pressed={pressed}
                      setPressed={setPressed}
                      handlePointConfirmation={handlePointConfirmation}
                      secondaryColor={secondaryColor}
                      discount={STORES_DISCOUNT_CYC[companyName] || 40}
                      height={'34vh'}
                      id="map-payment-button"
                    />
                  }
                </div>
              )}
            </div>
          </div>
          <div className='hidden md:block h-full'>
            <div className={`flex flex-col ${ecommerceInfo.cms === 'woocommerce' ? 'bg-slate-100 md:bg-white' : 'bg-slate-100'} md:h-[380px] md:w-[785px]`}>
              <div className='border-2 rounded-xl my-1 ml-4 md:w-[747px] md:h-[44px] bg-white'>
                {selectedShippingMethod && (
                  <ReturnPickupPoint />
                )}
              </div>
              <div className={`flex flex-row pl-3 ${ecommerceInfo.cms === 'woocommerce' ? 'bg-slate-100 md:bg-white' : 'bg-slate-100'}`}>
                <div
                  className='w-[366px] h-[300px] shadow-[10px_10px_15px_3px_rgba(0,0,0,0.1)]'
                  style={{ zIndex: 1500000 }}
                >
                  <Autocomplete
                    onPlaceSelected={handlePlaceSelected}
                    showNoResultsAction={true}
                  />
                  <div className='bg-white rounded w-full h-[254px]'>
                    {!isReady ? <div className='flex flex-col justify-center md:h-full md:w-full'> <Spinner /> </div>
                      : <PickupPointsList
                        points={filteredPoints}
                        onPointSelection={handlePointSelection}
                        markerSelectedId={markerSelectedId}
                        setMarkerSelectedId={setMarkerSelectedId}
                        isLocalPickup={isLocalPickup}
                        handlePointConfirmation={handlePointConfirmation}
                        secondaryColor={secondaryColor}
                        discount={STORES_DISCOUNT_CYC[companyName] || 40}
                      />
                    }
                  </div>
                </div>
                <div className='w-[381px] h-[250px] relative'>
                  <div className='rounded-xl border-2'>
                    <Map
                      markerSource={{ features: orderedPoints }}
                      markerSelectedId={markerSelectedId}
                      onPointSelection={handlePointSelection}
                      center={mapCenter}
                      height='294px'
                      setIsReady={setIsReady}
                      mapboxToken={companyInfo.mapboxToken}
                    />
                  </div>
                  {markerSelectedId && (
                    <div className='absolute -bottom-10 z-50 left-1/2 -translate-x-1/2'>
                      {companyInfo.companyName === 'Agua de Coco' && isLocalPickup && <span className='flex justify-center text-center bg-white text-sm'>Envíos a tienda MIE o VIE. <br />Avisamos telefónicamente su llegada.</span>}
                      <PointDataConfirmation
                        pointInfo={pointInfo}
                        showDiscountPrice={true}
                        label='Elegir Punto'
                        setMarkerSelectedId={setMarkerSelectedId}
                        secondaryColor={secondaryColor}
                        handlePointConfirmation={handlePointConfirmation}
                        isLocalPickup={isLocalPickup}
                        discount={STORES_DISCOUNT_CYC[companyName] || 40}
                        testId='select-point'
                        id="payment-button-map"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      }
    </>
  )
}

export default PickupMap
